import React, { useContext, memo } from 'react';
import PropTypes from 'prop-types';
import { Breadcrumbs, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import { NavigateNext } from '@material-ui/icons';
import { useRouteMatch } from 'react-router';

import { WebContext } from 'src/scenes/web-context';
import { arraysAreEqual, urlJoin } from 'src/utils/util';
import { upperCaseOnlyFirstLetter as ucofl } from 'src/utils/formatters';


const useStyles = makeStyles(theme => ({
  currentLink: {
    cursor: 'pointer',
    fontWeight: 500,
  },
  root: {
    ...theme.typography.body2,
  },
}));

const projectsBaseUrl = '/web/projects';

const baseUrl = '/web/company-summary';
const monitoringPlanBaseUrl = ({ id, siteId }) => `/web/projects/${id}${siteId ? `/sites/${siteId}` : ''}/monitoring-plans`;

const strOrTripleDots = str => ucofl(str) || '...';

const urls = {
  summary: () => ({ name: 'Inicio', url: baseUrl }),
  dashboard: () => ({ name: 'Resumen', url: projectsBaseUrl }), // TODO: arreglar this (buscar 'dashboard' en vscode)
  company: companyName => ({ name: companyName, url: projectsBaseUrl }),
  project: ({ id, name }) => ({ name: strOrTripleDots(name), url: urlJoin(projectsBaseUrl, id) }),
  sites: ({ id }) => ({ name: 'Sitios', url: urlJoin(projectsBaseUrl, id, 'sites') }),
  site: ({ id, siteId, siteName }) =>
    ({ name: strOrTripleDots(siteName), url: urlJoin(projectsBaseUrl, id, 'sites', siteId, 'land') }),
  'monitoring-plans': ({ id, siteId, name, siteName }) =>
    ({ name: `Planes de monitoreo de ${strOrTripleDots(siteId ? siteName : name)}`,
      url: monitoringPlanBaseUrl({ id, siteId }) }),
  projects: () => ({ name: 'Proyectos', url: projectsBaseUrl }),
  campaigns: ({ id, siteId, siteName, name }) => ({ name: `Campañas de ${strOrTripleDots(siteId ? siteName : name)}`,
    url: urlJoin(projectsBaseUrl, id, siteId ? `sites/${siteId}` : '', 'campaigns') }),
};

const NviroBreadcrumbs = ({ breadcrumbs, project, monitoringPlanName, ...props }) => {
  const context = useContext(WebContext);
  const match = useRouteMatch();
  const classes = useStyles();
  const { projectId: id, siteId } = match.params;
  const projectInfo = { id, siteId, monitoringPlanName, ...project };

  const breadcrumbsToShow = breadcrumbs.map(breadcrumb => {
    if (!project.siteId && [ 'site', 'sites' ].includes(breadcrumb)) {
      return;
    }
    return typeof breadcrumb === 'string' ?
      breadcrumb === 'company' ? urls[breadcrumb](context.selectedCompany.name) : urls[breadcrumb]({ ...context, ...projectInfo })
      : breadcrumb;
  }).filter(f => f);
  const lastBreadcrumbIndex = breadcrumbsToShow.length - 1;

  return (
    <Breadcrumbs maxItems={5} className={classes.root} separator={<NavigateNext fontSize="small" />} aria-label="breadcrumb" {...props}>
      {breadcrumbsToShow.map((breadcrumb, idx) =>
        idx === lastBreadcrumbIndex ?
          <Link key={idx} color="textPrimary" className={classes.currentLink} >{breadcrumb.name}</Link> :
          <Link key={idx} color="inherit" {...(breadcrumb.url ? { to: breadcrumb.url, component: RouterLink } : {})}>
            {breadcrumb.name}
          </Link>,
      )}
    </Breadcrumbs>
  );
};

NviroBreadcrumbs.propTypes = {
  children: PropTypes.any,
  classes: PropTypes.object,
  breadcrumbs: PropTypes.array.isRequired,
  history: PropTypes.any,
  project: PropTypes.shape({
    name: PropTypes.string,
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    id: PropTypes.number,
  }),
  monitoringPlanName: PropTypes.string,
};

const areEqual = (prevProps, nextProps) => arraysAreEqual(prevProps.breadcrumbs, nextProps.breadcrumbs);

const MemoizedNviroBreadcrumbs = memo(NviroBreadcrumbs, areEqual);


export {
  MemoizedNviroBreadcrumbs as NviroBreadcrumbs,
};
